import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  BookmarkCreate,
  DirectoryHeaderPadding,
  FormField,
  FormSelect,
  useSystemClassifierSelectOptions,
  PeriodEndPicker,
  Frequency,
  parseGroupDescriptionRef,
  getGroupDescriptionType,
  useGroupDescriptionOptions,
  DataObjectVersionSelect,
  FormSubmitButton,
  RelationsSearchGroupByType,
  FormCheckbox,
  DataObjectVersionOptionLabel,
  DataObjectVersionOptionBadge,
  useDataObjectVersionsQuery,
  DataObjectVersionType,
  DataObjectGroupsWithDescriptionsMapById,
  updateFrequency,
  InfoIcon,
  InfoIconUrl,
  formatDateTimeShort,
} from '@atlas-frontend/sdk';
import { keepPreviousData } from '@tanstack/react-query';
import { Flex } from 'antd';
import translations from './RelationsHeader.translations.json';
import React, { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { Dayjs } from 'dayjs';

const { addTranslations, useTranslation } = i18nNsFactory('relations-header');
addTranslations(translations);

export type RelationsHeaderProps = {
  groupsWithDescriptions?: DataObjectGroupsWithDescriptionsMapById;
  isFetchingGroupsWithDescriptions: boolean;
  isFetchingData: boolean;
  periodOptions: ValidPeriodOptions;
  setPeriodOptions: (periodOptions: ValidPeriodOptions) => void;
};

interface ValidPeriodOptions {
  frequency?: Frequency;
  isDisabledDate?: (date: Dayjs) => boolean;
  validRanges?: Array<[Dayjs | null, Dayjs | null]>;
  userFollowedGroupPeriodSkipCount?: number;
}

export function RelationsHeader({
  groupsWithDescriptions,
  isFetchingGroupsWithDescriptions,
  isFetchingData,
  periodOptions,
  setPeriodOptions,
}: RelationsHeaderProps) {
  const { t } = useTranslation();

  const {
    REL_SRCH_DATA_OBJ_TYPE: typeOptions,
    REL_SRCH_GRP_TYPE: groupByOptions,
  } = useSystemClassifierSelectOptions(
    ['REL_SRCH_DATA_OBJ_TYPE', 'REL_SRCH_GRP_TYPE'],
    'code',
  );

  const formValues = useWatch();
  const { groupOrDescriptionId, valueDate } = formValues;
  const { control, getValues, setValue } = useFormContext();

  const groupDescriptionType = useMemo(
    () =>
      !!groupOrDescriptionId
        ? getGroupDescriptionType(groupOrDescriptionId)
        : undefined,
    [groupOrDescriptionId],
  );
  const { data: versions } = useDataObjectVersionsQuery(
    {
      id:
        !!groupOrDescriptionId && typeof groupOrDescriptionId !== 'number'
          ? parseGroupDescriptionRef(groupOrDescriptionId).descriptionId || 0
          : 0,
      valueDate: valueDate ? valueDate.format('YYYY-MM-DD') : undefined,
    },
    {
      enabled:
        !!groupOrDescriptionId &&
        groupDescriptionType === 'description' &&
        !!valueDate,
      placeholderData: keepPreviousData,
    },
  );

  const [
    descriptionGroupOptions,
    ungroupedDescriptionOptions,
    groupedDescriptionOptions,
  ] = useGroupDescriptionOptions(groupsWithDescriptions);

  const groupByOptionsFiltered = useMemo(
    () =>
      groupDescriptionType === 'group'
        ? groupByOptions
        : groupByOptions.filter(
            (option) => option.value !== RelationsSearchGroupByType.SELECTED,
          ),
    [groupDescriptionType, groupByOptions],
  );

  useEffect(() => {
    if (groupOrDescriptionId) {
      const { groupId, descriptionId } =
        parseGroupDescriptionRef(groupOrDescriptionId);
      const objectType = getGroupDescriptionType(groupOrDescriptionId);

      const descriptionGroupId =
        objectType === 'description' && groupsWithDescriptions
          ? Array.from(groupsWithDescriptions.values()).find((item) =>
              item.descriptions.some((desc) => desc.id === descriptionId),
            )?.id
          : undefined;

      updateFrequency(
        objectType,
        descriptionId || groupId,
        setPeriodOptions,
        groupsWithDescriptions,
        descriptionGroupId,
      );
      if (objectType === 'description') {
        setValue(
          'groupBy',
          (getValues('groupBy') || []).filter(
            (item: string) => item !== RelationsSearchGroupByType.SELECTED,
          ),
        );
      }
    }
  }, [
    groupOrDescriptionId,
    groupsWithDescriptions,
    getValues,
    setValue,
    setPeriodOptions,
  ]);

  return (
    <DirectoryHeaderPadding horizontal top>
      <Flex gap="middle" align="center" justify="flex-start">
        <Title>{t('title')}</Title>
        <InfoIcon infoIconUrl={InfoIconUrl.RELATIONS_DIRECTORY}></InfoIcon>
        <BookmarkCreate
          excludeUrlParams={['valueDate']}
          includeUrlParams={[{ key: 'periodSkipLastValueDate', value: 'true' }]}
        />
        <Flex gap="middle" align="flex-start" wrap="wrap">
          <FormField name="type" label={t('type.label')} size="small">
            <FormSelect
              name="type"
              placeholder={t('type.placeholder')}
              styles={{
                popup: { root: { width: 'max-content' } },
              }}
              options={typeOptions}
              allowClear
              onChange={() => {
                setValue('groupOrDescriptionId', undefined);
                setValue('valueDate', undefined);
                setValue('version', null);
                setValue('groupBy', undefined);
                setPeriodOptions({});
              }}
            />
          </FormField>

          <FormField
            name="groupOrDescriptionId"
            label={t('groupOrDescriptionId.label')}
            size="small"
            required
          >
            <FormSelect
              name="groupOrDescriptionId"
              placeholder={t('groupOrDescriptionId.placeholder')}
              styles={{
                popup: { root: { width: 'max-content', maxHeight: 400 } },
              }}
              showSearch
              allowClear
              disabled={isFetchingGroupsWithDescriptions}
              loading={isFetchingGroupsWithDescriptions}
              options={
                isFetchingGroupsWithDescriptions
                  ? []
                  : [
                      {
                        key: 'description-groups',
                        label: t('option-group.description-groups'),
                        options: descriptionGroupOptions,
                      },
                      {
                        key: 'grouped-descriptions',
                        label: t('option-group.grouped-descriptions'),
                        options: groupedDescriptionOptions,
                      },
                      {
                        key: 'ungrouped-descriptions',
                        label: t('option-group.ungrouped-descriptions'),
                        options: ungroupedDescriptionOptions,
                      },
                    ]
              }
              filterOption={(input, option) =>
                Boolean(
                  option?.label?.toLowerCase().includes(input.toLowerCase()),
                )
              }
              onChange={(newValue) => {
                if (!newValue) {
                  setValue('valueDate', undefined);
                  setPeriodOptions({});
                }
                setValue('version', null);
                setValue('groupBy', undefined);
              }}
            />
          </FormField>

          <FormField
            name="valueDate"
            size="small"
            label={t('valueDate.label')}
            required
          >
            <Controller
              control={control}
              name={'valueDate'}
              render={({ field: { value, onChange } }) => (
                <PeriodEndPicker
                  value={value}
                  disabled={!groupOrDescriptionId}
                  disabledDate={periodOptions?.isDisabledDate}
                  onChange={(val) => {
                    onChange(val);
                    setValue('version', null);
                  }}
                  frequency={periodOptions?.frequency}
                />
              )}
            />
          </FormField>

          <FormField name="version" size="small" label={t('version.label')}>
            {groupDescriptionType === 'description' ? (
              <Controller
                control={control}
                name={'version'}
                render={({ field: { value, onChange } }) => (
                  <DataObjectVersionSelect<DataObjectVersionType>
                    activeVersionId={value ?? null}
                    versions={versions || []}
                    onChange={(v) => onChange(v ?? null)}
                    disabled={
                      !groupOrDescriptionId ||
                      !valueDate ||
                      isFetchingGroupsWithDescriptions
                    }
                    placeholder={t('version.placeholder')}
                    wrapperHeight="auto"
                    allowClear
                    sortVersions={(versions) =>
                      [...(versions || [])].sort(
                        (a: DataObjectVersionType, b: DataObjectVersionType) =>
                          new Date(b.calculationTime ?? 0).getTime() -
                          new Date(a.calculationTime ?? 0).getTime(),
                      )
                    }
                    renderOption={(v) => ({
                      label: (
                        <DataObjectVersionOptionLabel
                          className={v.statusCode || 'DRAFT'}
                          gap="small"
                        >
                          {(v.versionNumber || v.minorNumber) && (
                            <>
                              <DataObjectVersionOptionBadge
                                count={`${v.versionNumber}${
                                  v?.minorNumber ? `.${v.minorNumber}` : ''
                                }`}
                                showZero
                                color="black"
                                size="small"
                              />{' '}
                            </>
                          )}
                          {formatDateTimeShort(v.calculationTime)}
                        </DataObjectVersionOptionLabel>
                      ),
                      value: v.id,
                      className: v.statusCode || 'DRAFT',
                    })}
                  />
                )}
              />
            ) : (
              <FormSelect
                name="version"
                placeholder={t('version.placeholder')}
                styles={{
                  popup: { root: { width: 'max-content' } },
                }}
                allowClear
                options={[{ value: 'latest', label: t('version.latest') }]}
                disabled={!groupOrDescriptionId}
              />
            )}
          </FormField>

          <FormField name="groupBy" label={t('groupBy.label')} size="small">
            <FormSelect
              name="groupBy"
              placeholder={t('groupBy.placeholder')}
              style={{ minWidth: '150px' }}
              styles={{
                popup: { root: { width: 'max-content' } },
              }}
              options={groupByOptionsFiltered}
              mode="multiple"
              disabled={!groupOrDescriptionId}
            />
          </FormField>

          <StyledFormField name="showOneStep">
            <FormCheckbox name="showOneStep">
              {t('showOneStep.label')}
            </FormCheckbox>
          </StyledFormField>

          <StyledFormField name="showIntermediates">
            <FormCheckbox name="showIntermediates">
              {t('showIntermediates.label')}
            </FormCheckbox>
          </StyledFormField>

          <StyledFormSubmitButton type="default" disabled={isFetchingData}>
            {t('search')}
          </StyledFormSubmitButton>
        </Flex>
      </Flex>
    </DirectoryHeaderPadding>
  );
}

const Title = styled.h1`
  align-self: center;
`;

const StyledFormField = styled(FormField)`
  align-self: center;
`;

const StyledFormSubmitButton = styled(FormSubmitButton)`
  align-self: center;
`;
